import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// NEW Tenures.co.uk Pages (Gov-Tech Design)
import B2BLanding from "@/pages/tenures/B2BLanding";
import ResidentPortal from "@/pages/tenures/ResidentPortal";
import SuccessionTriage from "@/pages/tenures/SuccessionTriage";
import LiveabilityTriage from "@/pages/tenures/LiveabilityTriage";
import RTBTriage from "@/pages/tenures/RTBTriage";
import ExchangeMatcher from "@/pages/tenures/ExchangeMatcher";
import MilestoneGuardian from "@/pages/tenures/MilestoneGuardian";
import ArrearsSupport from "@/pages/tenures/ArrearsSupport";
import B2BDashboard from "@/pages/tenures/B2BDashboard";
import LetterWriter from "@/pages/tenures/LetterWriter";

// Legacy Pages (for reference, can be removed later)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import SuccessionModule from "@/pages/SuccessionModule";
import ExchangeModule from "@/pages/ExchangeModule";
import DownsizingModule from "@/pages/DownsizingModule";
import UnlockModule from "@/pages/UnlockModule";
// Static Pages
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import Library from "@/pages/Library";
import ForProviders from "@/pages/ForProviders";
import Privacy from "@/pages/Privacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return children({ user, setUser, loading, checkAuth });
};

// Auth Callback Component for Google OAuth
const AuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, 
          { session_id: sessionId },
          { withCredentials: true }
        );

        const userData = response.data.user;
        setUser(userData);
        
        // Store a flag to indicate we're authenticated via Google
        localStorage.setItem('auth_type', 'google');
        
        toast.success(`Welcome, ${userData.name}!`);

        if (userData.onboarding_complete) {
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          navigate('/onboarding', { replace: true, state: { user: userData } });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-400">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check if user data is passed from AuthCallback
  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to onboarding if not complete
  if (!user.onboarding_complete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Main App Router
function AppRouter() {
  const location = useLocation();

  return (
    <AuthContext>
      {({ user, setUser, loading, checkAuth }) => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        // Check URL fragment for session_id (OAuth callback)
        if (location.hash?.includes('session_id=')) {
          return <AuthCallback setUser={setUser} />;
        }

        return (
          <Routes>
            {/* NEW Tenures.co.uk Routes (Gov-Tech Design - Privacy First) */}
            <Route path="/" element={<B2BLanding />} />
            <Route path="/resident" element={<ResidentPortal />} />
            <Route path="/succession-triage" element={<SuccessionTriage />} />
            <Route path="/liveability-triage" element={<LiveabilityTriage />} />
            <Route path="/rtb-triage" element={<RTBTriage />} />
            <Route path="/exchange-matcher" element={<ExchangeMatcher />} />
            <Route path="/milestone-guardian" element={<MilestoneGuardian />} />
            <Route path="/arrears-support" element={<ArrearsSupport />} />
            <Route path="/letter-writer" element={<LetterWriter />} />
            <Route path="/dashboard" element={<B2BDashboard />} />
            
            {/* Static Pages */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/library" element={<Library />} />
            <Route path="/providers" element={<ForProviders />} />
            <Route path="/privacy" element={<Privacy />} />
            
            {/* Legacy Routes (keeping for now, can be removed) */}
            <Route path="/legacy" element={<Landing />} />
            <Route path="/login" element={
              user ? <Navigate to="/dashboard" replace /> : <Login setUser={setUser} />
            } />
            <Route path="/register" element={
              user ? <Navigate to="/dashboard" replace /> : <Register setUser={setUser} />
            } />
            <Route path="/onboarding" element={
              <ProtectedRoute user={user} loading={loading}>
                <Onboarding user={user || location.state?.user} setUser={setUser} checkAuth={checkAuth} />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute user={user} loading={loading}>
                <Dashboard user={user || location.state?.user} setUser={setUser} />
              </ProtectedRoute>
            } />
            <Route path="/succession" element={
              <ProtectedRoute user={user} loading={loading}>
                <SuccessionModule user={user || location.state?.user} />
              </ProtectedRoute>
            } />
            <Route path="/exchange" element={
              <ProtectedRoute user={user} loading={loading}>
                <ExchangeModule user={user || location.state?.user} />
              </ProtectedRoute>
            } />
            <Route path="/downsizing" element={
              <ProtectedRoute user={user} loading={loading}>
                <DownsizingModule user={user || location.state?.user} />
              </ProtectedRoute>
            } />
            <Route path="/unlock" element={
              <ProtectedRoute user={user} loading={loading}>
                <UnlockModule user={user || location.state?.user} />
              </ProtectedRoute>
            } />
          </Routes>
        );
      }}
    </AuthContext>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
